/**
 * Drawer «Проблемы» — открывается по клику на бейдж ⚠ в TopBar.
 *
 * Показывает HealthAspect-чекеры сгруппировано: заголовок чекера +
 * список findings с severity-иконкой. Клик по finding'у:
 *   - если есть anchor (finding привязан к narrative-блоку) — скроллит
 *     к блоку в Canvas-view и закрывает панель;
 *   - иначе — открывает EntityCard drawer для этой сущности.
 */

import { Badge, Box, Code, Divider, Drawer, Group, ScrollArea, Stack, Text, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useState } from 'react';
import type { HealthAspect, HealthFinding, Severity, SnapshotDiagnostics } from '@pflow/shared/v2';
import { useUiStore } from '../stores/ui-store';
import { DESKTOP_QUERY } from '../lib/breakpoints';

const SEVERITY_COLOR: Record<Severity, string> = {
    ok:        'green',
    neutral:   'gray',
    attention: 'yellow',
    critical:  'red',
};

const SEVERITY_ICON: Record<Severity, string> = {
    ok:        '✓',
    neutral:   '·',
    attention: '⚠️',
    critical:  '🚨',
};

function anchorFromFinding(f: HealthFinding): string | null {
    if (!f.anchor) return null;
    const { aspectId, scope } = f.anchor;
    const scopePart = scope.kind === 'project'
        ? 'project'
        : `canvas-${scope.canvas.toLowerCase().replace(/\s+/g, '-')}`;
    return `aspect-${scopePart}-${aspectId.replace(/\./g, '-')}`;
}

interface Props {
    health:       HealthAspect[];
    diagnostics?: SnapshotDiagnostics;
    opened:       boolean;
    onClose:      () => void;
}

export function HealthPanel({ health, diagnostics, opened, onClose }: Props): JSX.Element {
    const isDesktop  = useMediaQuery(DESKTOP_QUERY, true);
    const triggerFlash = useUiStore(s => s.triggerFlash);
    const openCard   = useUiStore(s => s.openCard);

    const nonEmpty = health.filter(h => h.findings.length > 0);

    function handleFinding(f: HealthFinding): void {
        const anchor = anchorFromFinding(f);
        if (anchor) {
            onClose();
            // небольшая задержка чтобы drawer успел закрыться до scroll
            requestAnimationFrame(() => {
                const el = document.getElementById(anchor);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                triggerFlash(anchor);
            });
        } else {
            openCard(f.entity);
            onClose();
        }
    }

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size={isDesktop ? 420 : '100%'}
            title={
                <Group gap="xs">
                    <Text fw={700}>Проблемы</Text>
                    <Badge color="red" variant="light" size="sm">
                        {health.reduce((s, h) => s + h.count, 0)}
                    </Badge>
                </Group>
            }
            styles={{
                content: { display: 'flex', flexDirection: 'column' },
                body:    { flex: 1, padding: 0, overflow: 'hidden' },
            }}
        >
            <ScrollArea h="100%" type="auto">
                {diagnostics && (diagnostics.errors > 0 || diagnostics.warnings > 0) && (
                    <Box px="md" pt="md" pb="xs">
                        <Group gap="xs" mb={4}>
                            <Text fw={600} fz="sm">Корректность модели</Text>
                            {diagnostics.errors > 0 && (
                                <Badge color="red" variant="light" size="xs">{diagnostics.errors} ошибок</Badge>
                            )}
                            {diagnostics.warnings > 0 && (
                                <Badge color="yellow" variant="light" size="xs">{diagnostics.warnings} предупр.</Badge>
                            )}
                        </Group>
                        <Text fz="xs" c="dimmed">
                            Авторинг-ошибки DSL (обязательные поля, ссылки, P&L). Детали и навигация — в редакторе/агенте.
                        </Text>
                        <Divider mt="sm" />
                    </Box>
                )}
                {nonEmpty.length === 0 ? (
                    <Box p="md">
                        <Text c="dimmed" fz="sm">Со здоровьем модели проблем не найдено.</Text>
                    </Box>
                ) : (
                    <Stack gap={0}>
                        {nonEmpty.map((aspect, i) => (
                            <Box key={aspect.id}>
                                {i > 0 && <Divider />}
                                <Box px="md" pt="md" pb="xs">
                                    <Group gap="xs" mb="xs">
                                        <Text fw={600} fz="sm">{aspect.title}</Text>
                                        <Badge
                                            color={SEVERITY_COLOR[aspect.severity]}
                                            variant="light"
                                            size="xs"
                                        >
                                            {aspect.count}
                                        </Badge>
                                    </Group>
                                    <Stack gap={4}>
                                        {aspect.findings.map((f, fi) => (
                                            <FindingRow
                                                key={fi}
                                                finding={f}
                                                onClick={() => handleFinding(f)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )}
            </ScrollArea>
        </Drawer>
    );
}

function FindingRow({ finding, onClick }: { finding: HealthFinding; onClick: () => void }): JSX.Element {
    const hasAnchor = !!finding.anchor;
    const icon      = SEVERITY_ICON[finding.severity];
    const color     = SEVERITY_COLOR[finding.severity];
    const [hovered, setHovered] = useState(false);

    return (
        <UnstyledButton
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display:      'block',
                width:        '100%',
                textAlign:    'left',
                borderRadius: 6,
                padding:      '6px 8px',
                background:   hovered ? 'var(--mantine-color-default-hover)' : undefined,
            }}
        >
            <Group gap="xs" wrap="nowrap" align="flex-start">
                <Text span fz="sm" style={{ flexShrink: 0 }}>{icon}</Text>
                <Box style={{ minWidth: 0 }}>
                    <Text fz="xs" c="dimmed" truncate>
                        {finding.entity.type} «{finding.entity.name}»
                        {finding.entity.canvas ? ` · ${finding.entity.canvas}` : ''}
                    </Text>
                    <Text fz="sm" c={color}>{finding.message}</Text>
                    {finding.fix && (
                        <Box mt={4}>
                            <Text fz="xs" c="dimmed">↳ как починить: {finding.fix.hint}</Text>
                            {finding.fix.example && (
                                <Code fz="xs" style={{ display: 'block', marginTop: 2, whiteSpace: 'pre-wrap' }}>
                                    {finding.fix.example}
                                </Code>
                            )}
                            {(finding.fix.field || finding.fix.see_tool) && (
                                <Text fz="xs" c="dimmed" mt={2}>
                                    {[
                                        finding.fix.field    ? `поле: ${finding.fix.field}`   : '',
                                        finding.fix.see_tool ? `tool: ${finding.fix.see_tool}` : '',
                                    ].filter(Boolean).join(' · ')}
                                </Text>
                            )}
                        </Box>
                    )}
                    {hasAnchor && (
                        <Text fz="xs" c="dimmed" mt={2}>↗ перейти к блоку</Text>
                    )}
                </Box>
            </Group>
        </UnstyledButton>
    );
}
